import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown, Shield, ShieldCheck, BadgeCheck, Anchor } from "lucide-react";
import type { FaqItem } from "@/lib/site";

export function Section({
  children,
  className = "",
  id,
  tone = "default",
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  tone?: "default" | "muted" | "primary";
}) {
  const bg = tone === "muted" ? "bg-secondary/40" : tone === "primary" ? "bg-primary text-primary-foreground" : "";
  return (
    <section id={id} className={`py-16 sm:py-24 ${bg} ${className}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[oklch(0.55_0.12_82)]">
      <span className="h-px w-6 bg-[oklch(0.78_0.12_82)]" />
      {children}
    </div>
  );
}

export function SectionHeading({ eyebrow, title, intro }: { eyebrow?: string; title: string; intro?: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {eyebrow && <div className="flex justify-center"><Eyebrow>{eyebrow}</Eyebrow></div>}
      <h2 className="font-serif text-3xl text-foreground sm:text-4xl">{title}</h2>
      {intro && <p className="mt-4 text-base text-muted-foreground sm:text-lg">{intro}</p>}
    </div>
  );
}

export function TrustBar() {
  const items = [
    { icon: ShieldCheck, label: "Licensed & Insured" },
    { icon: BadgeCheck, label: "Background-Checked" },
    { icon: Shield, label: "Bonded for Your Protection" },
    { icon: Anchor, label: "Locally Owned in Sarasota" },
  ];
  return (
    <div className="border-y border-border bg-secondary/30">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-6 sm:grid-cols-4 sm:px-6 lg:px-8">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2.5 text-sm text-foreground/80">
            <Icon className="h-5 w-5 text-[oklch(0.55_0.12_82)]" />
            <span className="font-medium">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2.5">
          <Check className="mt-0.5 h-5 w-5 flex-none text-[oklch(0.55_0.12_82)]" />
          <span className="text-sm text-foreground/85">{i}</span>
        </li>
      ))}
    </ul>
  );
}

export function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="mx-auto max-w-3xl divide-y divide-border rounded-xl border border-border bg-card">
      {items.map((f, idx) => (
        <details key={idx} className="group p-5">
          <summary className="flex cursor-pointer items-center justify-between gap-4 text-left font-medium text-foreground">
            {f.q}
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export function CtaBand({ title, sub }: { title: string; sub?: string }) {
  return (
    <section className="bg-primary text-primary-foreground">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-12 sm:px-6 lg:flex-row lg:items-center lg:px-8">
        <div>
          <h3 className="font-serif text-2xl sm:text-3xl">{title}</h3>
          {sub && <p className="mt-1 text-primary-foreground/75">{sub}</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="rounded-md bg-[oklch(0.78_0.12_82)] px-5 py-3 text-sm font-semibold text-[oklch(0.22_0.04_250)] transition hover:brightness-105"
          >
            Get a Free Quote
          </Link>
          <a
            href="tel:+19415002320"
            className="rounded-md border border-primary-foreground/30 px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary-foreground/10"
          >
            Call (941) 500-2320
          </a>
        </div>
      </div>
    </section>
  );
}